import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { atualizarPagador } from '../../src/services/pagadores';
import type { PagadorUc } from '../../src/lib/pagadorUc';

// #6565: o cliente da UC mudou de nome e o pagador do boleto, com o mesmo CPF/CNPJ,
// ficou com o nome antigo. Trava a tela até a pessoa escolher: o boleto sai pelo
// nome do pagador, e passar batido aqui é o que deixou o boleto com o nome errado.

const PagadorOutroNomeModal: React.FC<{
    pagador: PagadorUc;
    nomeCliente: string;
    totalUcs: number;
    usuarioId?: string | null;
    motivo: string | null;
    onFim: (atualizou: boolean) => void;
}> = ({ pagador, nomeCliente, totalUcs, usuarioId, motivo, onFim }) => {
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const atualizar = async () => {
        setSalvando(true); setErro(null);
        try {
            await atualizarPagador(
                pagador.id,
                { nome: nomeCliente, cpf_cnpj: pagador.cpf_cnpj ?? '', email: pagador.email, whatsapp: pagador.whatsapp },
                usuarioId,
                motivo || 'Pagador acompanhou o novo nome do cliente (mesmo CPF/CNPJ).',
            );
            onFim(true);
        } catch (e: any) {
            setErro(e?.message || 'Não foi possível atualizar o pagador.');
            setSalvando(false);
        }
    };

    return createPortal(
        // Sem fechar por clique fora nem Esc: a pessoa precisa escolher.
        <div className="fixed inset-0 z-[3100] bg-black/50 flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
            <div role="alertdialog" aria-modal="true" aria-labelledby="pagador-outro-nome-titulo" aria-describedby="pagador-outro-nome-texto"
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg p-6">
                <h3 id="pagador-outro-nome-titulo" className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-500" /> O boleto ainda sai com o nome antigo
                </h3>
                <div id="pagador-outro-nome-texto" className="text-sm text-gray-600 dark:text-gray-300 mt-2 space-y-2">
                    <p>
                        O pagador do boleto tem o mesmo CPF/CNPJ do cliente, mas está como <strong className="text-gray-900 dark:text-white">{pagador.nome}</strong>.
                        O cliente agora é <strong className="text-gray-900 dark:text-white">{nomeCliente}</strong>.
                    </p>
                    {totalUcs > 1 ? <p>Este pagador é usado em {totalUcs} UCs. Atualizar muda o nome em todas.</p> : null}
                </div>
                {erro ? <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{erro}</p> : null}
                <div className="mt-5 flex flex-col-reverse sm:flex-row justify-end gap-2">
                    <button type="button" disabled={salvando} onClick={() => onFim(false)}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50">
                        Manter como está
                    </button>
                    <button type="button" autoFocus disabled={salvando} onClick={atualizar}
                        className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-primary hover:bg-primary/90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                        {salvando ? <Loader2 size={14} className="animate-spin" /> : null} Atualizar pagador
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default PagadorOutroNomeModal;
